
import ArticleTemplate from "@/components/ArticleTemplate";
import { Helmet } from "react-helmet";
import { useState, useEffect } from "react";
import ImageOverlay from "@/components/common/ImageOverlay";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const KulutusluottoVertailuArticlePage = () => {
  // Use a state for the hero image
  const [heroImage, setHeroImage] = useState("");
  const [imageLoaded, setImageLoaded] = useState(false);

  // Set initial image with a fallback system
  useEffect(() => {
    // Try to use a Supabase stored image first
    const initialImage = "/lovable-uploads/1c9feb20-4f8f-47cc-a973-32b2372e8f1b.png";
    
    // Fallback images if the primary one fails
    const fallbackImages = [
      "/lovable-uploads/5def2485-63c7-4f14-9d14-f2e4ea5e0347.png",
      "https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2070&auto=format&fit=crop"
    ];
    
    // Try to load the initial image
    setHeroImage(initialImage);
    
    // Preload image
    const img = new Image();
    let currentTry = 0;
    
    img.onload = () => {
      console.log("Image loaded successfully:", img.src);
      setHeroImage(img.src);
      setImageLoaded(true);
    };
    
    img.onerror = () => {
      console.error("Failed to load image:", img.src);
      if (currentTry < fallbackImages.length) {
        img.src = fallbackImages[currentTry];
        currentTry++;
      } else {
        toast.error("Kuvien lataus epäonnistui. Käytetään varmuuskuvaa.");
        setImageLoaded(true);
      }
    };
    
    img.src = initialImage;
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, []);
  
  const relatedArticles = [
    {
      id: "lainan-ottamisen-opas-2025",
      title: "Lainan ottamisen opas 2025",
      slug: "lainan-ottamisen-opas-2025",
      description: "Kattava yleisopas lainan prosessista, kilpailutuksesta ja parhaiden tarjousten hyödyntämisestä vuonna 2025."
    },
    {
      id: "yhdistelylaina-opas",
      title: "Yhdistelylaina-opas lainojen yhdistämiseen",
      slug: "yhdistelylaina-opas-lainojen-yhdistamiseen",
      description: "Kattava opas yhdistelylainaan: miten yhdistää useat lainat yhdeksi edullisemmaksi kokonaisuudeksi."
    }
  ];

  const handleImageChange = (newImageUrl: string) => {
    console.log("Image updated to:", newImageUrl);
    setHeroImage(newImageUrl);
    setImageLoaded(true);
    toast.success("Artikkelin kuva päivitetty onnistuneesti!");
  };

  return (
    <>
      <Helmet>
        <title>Kulutusluottojen vertailu - Säästä korkokuluissa | Lainasampo.fi</title>
        <meta 
          name="description" 
          content="Kulutusluottojen korot ja ehdot vaihtelevat paljon. Vertaile eri kulutusluottoja ja säästä jopa satoja euroja korkokuluissa." 
        />
        <meta property="og:image" content={heroImage} />
      </Helmet>

      <ArticleTemplate
        title="Kulutusluottojen vertailu - Säästä korkokuluissa"
        description="Kulutusluottojen korot ja ehdot vaihtelevat paljon. Tässä artikkelissa vertailemme eri kulutusluottoja ja kerromme, miten voit säästää jopa satoja euroja korkokuluissa."
        date="2025-01-25"
        readTime="7 min"
        category="Kulutusluotot"
        imageUrl={heroImage}
        relatedArticles={relatedArticles}
        onImageChange={handleImageChange}
        showEditButton={true}
        content={
          <>
            <div className="prose prose-blue lg:prose-lg max-w-none">
              <div className="bg-blue-50 p-6 rounded-lg mb-8 border-l-4 border-blue-500">
                <h2 className="text-xl font-semibold text-blue-700 mt-0">Miksi kilpailuttaa kulutusluotto?</h2>
                <p className="mb-0">
                  Kulutusluottojen korkoeroja vertailemalla voit säästää merkittävästi rahaa. Suomalaisten pankkien 
                  kulutusluottojen koroissa voi olla jopa <strong className="text-blue-700">5-10 prosenttiyksikön eroja</strong>, 
                  mikä tarkoittaa tuhansien eurojen säästöjä laina-aikana!
                </p>
              </div>

              <p>
                Kulutusluottoa hakiessa moni ottaa lainan suoraan omasta pankistaan vertailematta vaihtoehtoja. 
                Tämä on usein kallis virhe. Esimerkiksi 10 000 euron lainassa viiden prosenttiyksikön korkoero 
                tarkoittaa viiden vuoden laina-ajalla yli 1 300 euron säästöä.
              </p>

              <h2 className="text-2xl font-bold text-blue-700 mt-8 mb-4">Kulutusluottojen vertailu - mistä aloittaa?</h2>
              
              <p>
                Kulutusluottoja on tarjolla monenlaisiin tarpeisiin:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 mb-8">
                <div className="bg-white p-5 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-100">
                  <h3 className="text-lg font-semibold text-blue-600 mb-2">Kertaluotto</h3>
                  <p className="text-gray-700 mb-3">
                    Kertaluotto on yleisin kulutusluoton muoto. Saat lainasumman kerralla tilillesi ja maksat 
                    sen takaisin tasaerissä, yleensä 1-10 vuoden aikana.
                  </p>
                  <ul className="list-disc pl-5 text-gray-700">
                    <li>Selkeä takaisinmaksusuunnitelma</li>
                    <li>Kiinteät kuukausierät</li>
                    <li>Soveltuu isompiin hankintoihin</li>
                  </ul>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-100">
                  <h3 className="text-lg font-semibold text-blue-600 mb-2">Joustoluotto</h3>
                  <p className="text-gray-700 mb-3">
                    Joustoluotto on kuin luottokortti ilman korttia. Sinulla on käytössäsi luottoraja, josta voit 
                    nostaa rahaa tarpeen mukaan ja maksat takaisin joustavasti.
                  </p>
                  <ul className="list-disc pl-5 text-gray-700">
                    <li>Joustava käyttää erilaisiin tarpeisiin</li>
                    <li>Maksat korkoa vain käytetystä luotosta</li>
                    <li>Voi nostaa uudelleen, kun olet maksanut takaisin</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-blue-700 mt-8 mb-4">Tärkeimmät vertailukriteerit kulutusluotoille</h2>
              
              <Accordion type="single" collapsible className="my-6">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-blue-700 font-semibold">1. Todellinen vuosikorko</AccordionTrigger>
                  <AccordionContent className="pl-4">
                    <p>
                      Todellinen vuosikorko on tärkein yksittäinen vertailukriteeri. Se sisältää kaikki luoton kustannukset: 
                      nimelliskoron, avausmaksut, tilinhoitomaksut ja muut kulut. Laki velvoittaa kaikkia luotonantajia 
                      ilmoittamaan todellisen vuosikoron, mikä tekee vertailusta helpompaa.
                    </p>
                    <p className="mt-2">
                      Varo mainoksia, joissa korostetaan vain kuukausierää tai nimelliskorkoa. Todellinen vuosikorko 
                      kertoo luoton todelliset kustannukset.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-blue-700 font-semibold">2. Laina-aika ja joustavuus</AccordionTrigger>
                  <AccordionContent className="pl-4">
                    <p>
                      Tarkista, kuinka joustavia luoton ehdot ovat:
                    </p>
                    <ul className="list-disc pl-6 mt-2 mb-2">
                      <li>Voiko maksuerää muuttaa laina-aikana?</li>
                      <li>Onko mahdollisuus lyhennysvapaaseen (maksutaukoihin)?</li>
                      <li>Voiko lainan maksaa pois ennenaikaisesti ilman lisäkuluja?</li>
                    </ul>
                    <p>
                      Joustavat ehdot voivat olla arvokkaita, jos taloudellinen tilanteesi muuttuu laina-aikana.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-blue-700 font-semibold">3. Muut kulut ja maksut</AccordionTrigger>
                  <AccordionContent className="pl-4">
                    <p>
                      Vaikka todellinen vuosikorko sisältää kaikki kulut, kannattaa silti tarkistaa erikseen:
                    </p>
                    <ul className="list-disc pl-6 mt-2 mb-2">
                      <li>Avausmaksu / nostopalkkio</li>
                      <li>Kuukausittainen tilinhoitomaksu</li>
                      <li>Laskutusmaksu (paperilaskuista veloitetaan usein erikseen)</li>
                      <li>Ennenaikaisen takaisinmaksun kulut</li>
                    </ul>
                    <p>
                      Joskus luotto alemmalla korolla mutta korkeammilla muilla kuluilla voi olla kalliimpi kuin 
                      hieman korkeakorkoisempi luotto ilman muita kuluja.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg my-8">
                <h2 className="text-2xl font-bold text-blue-700 mt-0 mb-4">Näin säästät satoja euroja korkokuluissa</h2>
                
                <p className="mb-4">
                  Kulutusluottojen kilpailuttaminen on nyt helpompaa kuin koskaan. Seuraa näitä askeleita:
                </p>
                
                <ol className="list-decimal pl-6 mt-4 space-y-3">
                  <li className="font-medium">
                    <span className="text-blue-700">Tarkista luottotietosi</span> - Varmista, että luottotietosi ovat kunnossa. 
                    Maksuhäiriömerkinnät vaikuttavat merkittävästi lainan saatavuuteen ja ehtoihin.
                  </li>
                  <li className="font-medium">
                    <span className="text-blue-700">Vertaile vähintään 3-5 tarjousta</span> - Kilpailuta laina aina usean pankin 
                    ja rahoituslaitoksen kesken. Lainavertailupalvelut kuten Lainasampo auttavat tässä.
                  </li>
                  <li className="font-medium">
                    <span className="text-blue-700">Lue ehdot huolellisesti</span> - Kiinnitä huomiota erityisesti korkomarginaaliin, 
                    laina-aikaan ja muihin kuluihin. Halvimmalta vaikuttava tarjous ei aina ole edullisin.
                  </li>
                  <li className="font-medium">
                    <span className="text-blue-700">Neuvottele paremmat ehdot</span> - Kun sinulla on useita tarjouksia, voit 
                    käyttää niitä neuvotteluvalttina ja pyytää parempaa tarjousta suoraan pankistasi.
                  </li>
                </ol>
                
                <div className="bg-white p-4 rounded-lg mt-6 border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-700 mb-2">Esimerkkisäästö kilpailuttamalla:</h3>
                  <p className="text-gray-800">
                    15 000 € kulutusluotto, 5 vuoden laina-aika:
                  </p>
                  <ul className="mt-2 mb-0">
                    <li><strong>Pankki A:</strong> 12% korko = kokonaiskulut 5 095 €</li>
                    <li><strong>Pankki B:</strong> 7% korko = kokonaiskulut 2 838 €</li>
                    <li><strong className="text-blue-700">Säästö kilpailuttamalla:</strong> 2 257 €</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-10 mb-6 text-center">
                <Link to="/kilpailuta">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-lg">
                    Kilpailuta kulutusluottosi tästä
                  </Button>
                </Link>
                <p className="text-sm text-gray-500 mt-2">Maksuton ja sitomaton - saat tarjoukset muutamassa minuutissa</p>
              </div>
            </div>
          </>
        }
      />
    </>
  );
};

export default KulutusluottoVertailuArticlePage;
